import React, { useMemo } from 'react';
import type { WebsiteHeaderLayout_VideoArch } from '@shared/types/WebsiteHeaderSettings.types';
import { EventNameNav, EventNameNavProps } from '../components/EventNameNav';
import { HeaderPhoto } from '../components/HeaderPhoto/HeaderPhoto';
import { VideoArch } from '@apps/guest/packages/layout-engine/widgets/Custom/components/VideoArch/VideoArch';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { InlineEditor } from '@shared/components';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { VideoArchProps } from '@apps/guest/packages/layout-engine/widgets/Custom/components/VideoArch/VideoArch.types';
import { Box } from '@withjoy/joykit';
import { isInIframe } from '@shared/utils/isInIframe';

interface VideoArchLayoutProps extends EventNameNavProps {
  websiteHeader: WebsiteHeaderLayout_VideoArch;
  showPlaceholder?: boolean;
}

const isPreview = isInIframe();

export const VideoArchLayout = (props: VideoArchLayoutProps) => {
  const { websiteHeader, ...restProps } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { video } = websiteHeader.headerPhoto;

  const { pageFontColor } = useColorPaletteProvider();

  const showPlaceholder = isPreview && !!websiteHeader.headerPhoto.presentationLayout;

  const isMobileScreen = useIsMobileScreen();
  const videoUrl = isMobileScreen && video?.mobileUrl ? video.mobileUrl : video?.desktopUrl;

  // Determine if compact view (similar to VideoBackgroundLayout logic)
  // For videoArch, we typically don't use compact view, but keeping the structure consistent
  const isCompactView = false;

  const { tq } = useTranslation('websiteHeader');

  const { photoProps, videoProps } = useMemo(
    () => ({
      photoProps: {
        photo: photo
          ? {
              ...photo,
            }
          : undefined,
        innerArchColor: 'transparent',
        outerArchColor: pageFontColor,
        showPlaceholder: false,
      },
      videoProps: {
        videoUrl,
        thumbnailUrl: video?.placeholderImageUrl,
        showPlaceholder,
        height: isMobileScreen ? '100%' : '100vh',
        isCompactView,
        isMobile: isMobileScreen && !!video?.mobileUrl,
      },
    }),
    [photo, pageFontColor, videoUrl, video?.placeholderImageUrl, showPlaceholder, isCompactView, isMobileScreen, video?.mobileUrl],
  );

  const handleEditPhotoClick = useEventCallback(() => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  const renderWrapper = useEventCallback((content: React.ReactNode, _context: { photoProps: VideoArchProps['photoProps']; videoProps: VideoArchProps['videoProps'] }) => {
    return (
      <Box
        position="absolute"
        bottom={0}
        left={0}
        width="100%"
        height={isMobileScreen ? '100%' : 'auto'}
        display="flex"
        justifyContent="center"
        zIndex="docked"
      >
        <InlineEditor
          elementLabel={tq`Photo`}
          wrapperType="actionInside"
          actionData={{
            editPhoto: handleEditPhotoClick,
          }}
          componentName="headerPhoto"
          pageName={restProps.page}
          pageSlug={restProps.pageSlug || ''}
          isEligibleForInlineEditing={true}
          wrapperCSS={{
            width: '100%',
            height: '100%',
          }}
          containerCSS={{
            height: '100%',
          }}
        >
          {content}
        </InlineEditor>
      </Box>
    );
  });

  // On mobile, wrap everything in a flex container to fill viewport height
  // This allows VideoArch to fill remaining space after the header
  if (isMobileScreen) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100dvh"
        width="100%"
      >
        <EventNameNav
          eventDisplayName={websiteHeader.eventDisplayName}
          {...restProps}
        >
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            minHeight={0}
            width="100%"
            position="relative"
          >
            <HeaderPhoto
              websiteHeader={websiteHeader}
              pageSlug={restProps.pageSlug}
              pageType={restProps.page}
              showPlaceholder={false}
            >
              <VideoArch
                photoProps={photoProps}
                videoProps={videoProps}
                renderWrapper={renderWrapper}
              />
            </HeaderPhoto>
          </Box>
        </EventNameNav>
      </Box>
    );
  }

  return (
    <EventNameNav
      eventDisplayName={websiteHeader.eventDisplayName}
      {...restProps}
    >
      <HeaderPhoto
        websiteHeader={websiteHeader}
        pageSlug={restProps.pageSlug}
        pageType={restProps.page}
        showPlaceholder={false}
      >
        <VideoArch
          photoProps={photoProps}
          videoProps={videoProps}
          renderWrapper={renderWrapper}
        />
      </HeaderPhoto>
    </EventNameNav>
  );
};

VideoArchLayout.displayName = 'VideoArchLayout';
